import React, { useEffect, useState } from "react";
import {
  Backdrop,
  Box,
  // CircularProgress,
  ListItemText,
  Menu,
  MenuItem,
} from "@mui/material";
import { AxiosResponse } from "axios";
import { errorToastMessage, toastMessage } from "../../utils/toast";
import http from "../../utils/http";
import AddVisitModal from "./AddVisitModal";
import AddPhaseFormModal from "./AddPhaseFormModal";
import RearrangeModal from "./RearrangeModal";
import Phases from "./Phases";
import Forms from "./Forms";
import { useNavigate, useParams } from "react-router-dom";
import { StudyTabContent } from "./style";
import AddRepeatingModal from "./AddRepeatingModal";
import DeleteConfirmModal from "../Common/UI/DeleteConfirm";
import AddSurveyModal from "./AddSurveyModal";
import { repeatedDataTypes } from "../../utils/question";
import LanguageTranslationModal from "./LanguageTranslationModal";
import { useAppSelector } from "../../Redux/hooks";
import Loader from "../Common/assets/Loader";
import CopyFormModal from "./CopyFormModal";
import AddTemplateModal from "./AddTemplateModal";
import { featuresMap } from "../../utils/features";
import { permissions } from "../../utils/roles";

type SelectedPhase = {
  id: string;
  type: string;
  eventType?: string;
  deleted: boolean;
};

export type StudyTabsPhaseData = {
  id: string;
  name: string;
  duration: string;
  position: number;
  description: string;
  type: string;
  hasForms?: boolean;
  /** Survey package: linked surveys exist but at least one has no forms. */
  hasPhaseIssues?: boolean;
};

type Props = {
  type: {
    buttonLabel: string;
    headerLabel: string;
    val: string; // "repeated_data" | "visit" | "survey" | "surveyPackage"
  };
  canEdit: boolean;
  canExport?: boolean;
};

const StudyTab = ({ type, canEdit, canExport }: Props) => {
  const messageMap: any = {
    visit: "Visit",
    repeated_data: "Repeating data",
    survey: "Survey",
  };
  const { id: studyId } = useParams();
  const navigate = useNavigate();

  const features = useAppSelector((state) => state.study.features);
  const languages = useAppSelector((state) => state.study.languages);

  const [phaseData, setPhaseData] = useState<StudyTabsPhaseData[]>([]);
  const [formsData, setFormsData] = useState<any>([]);
  const [formName, setFormName] = useState("");
  const [selectedPhase, setSelectedPhase] = useState<SelectedPhase>({
    id: "",
    type: "",
    eventType: "",
    deleted: false,
  });
  const [loading, setLoading] = useState(true);
  const [anchorEl, setAnchorEl] = useState<any>(null);
  const open = Boolean(anchorEl);
  const [showModal, setShowModal] = useState("");
  const [phaseToggleLoader, setPhaseToggleLoader] = useState(false);
  const [formToggleLoader, setFormToggleLoader] = useState(false);
  const [rowData, setRowData] = useState<any>(null);
  const [triggerEvent, setTriggerEvent] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    title: string;
    description: string;
  }>({
    title: "",
    description: "",
  });

  useEffect(() => {
    const fetchPhaseData = async () => {
      try {
        setLoading(true);
        const res: AxiosResponse = await http.get(
          `/study/${studyId}/study-phase?category=${type.val}`
        );
        const data = res.data?.data;
        const formattedData = data
          ?.map((item: any) => ({
            id: item?.id,
            name: item?.name || "",
            duration: item?.duration ?? "",
            position: item?.position || 1,
            description: item?.description || "",
            introText: item?.introText || "",
            outroText: item?.outroText || "",
            remarks: item?.remarks || "",
            type: item?.type || "",
            hasForms: (item?.phaseForms?.length ?? 0) > 0,
            formIds:
              item?.phaseForms.length > 0
                ? item?.phaseForms?.map((item: any) => item?.form?.id)
                : [],
          }))
          .sort((a: any, b: any) => a.position - b.position);

        const session = sessionStorage?.getItem("selected-phase-id");
        let selected: any = null;
        if (session) {
          selected = formattedData.find((phase: any) => session === phase?.id);
        }
        sessionStorage.removeItem("selected-phase-id");

        setPhaseData(formattedData || []);
        setSelectedPhase((prev) =>
          prev.type === type.val
            ? { ...prev }
            : selected
            ? {
                id: selected?.id,
                name: selected?.name,
                type: selected?.id ? type.val : "",
                eventType: selected?.id ? selected?.type : "",
                deleted: !selected?.id,
              }
            : {
                id: formattedData?.[0]?.id,
                name: formattedData?.[0]?.name,
                type: formattedData?.[0]?.id ? type.val : "",
                eventType: formattedData?.[0]?.id
                  ? formattedData?.[0]?.type
                  : "",
                deleted: !formattedData?.[0]?.id,
              }
        );
        // setLoading(false);
      } catch (err) {
        errorToastMessage(err as Error);
        setLoading(false);
      }
    };
    if (type.val) {
      fetchPhaseData();
    }
  }, [phaseToggleLoader, type.val, studyId]);

  useEffect(() => {
    const fetchFormsData = async () => {
      try {
        setLoading(true);
        const res: AxiosResponse = await http.get(
          `/study/${studyId}/study-phase/${selectedPhase?.id}`
        );
        const data = res.data?.data;
        setFormName(data?.name || "");

        const newData = data?.phaseForms
          .map((item: any) => ({
            id: item?.id,
            name: item?.form?.name || "",
            description: item?.form?.description || "",
            position: item?.position || 1,
            formId: item?.form?.id || "",
            surveyPackageId: item?.form?.surveyPackageId || "",
            questionCount: item?.form?.questionCount || 0,
          }))
          .sort((a: any, b: any) => a.position - b.position);

        setFormsData(newData || []);
        setPhaseData((prev) =>
          prev.map((phase) =>
            phase.id === selectedPhase?.id
              ? {
                  ...phase,
                  hasForms: (newData?.length ?? 0) > 0,
                }
              : phase
          )
        );
        setLoading(false);
      } catch (err) {
        errorToastMessage(err as Error);
        setLoading(false);
      }
    };
    if (selectedPhase?.id) {
      fetchFormsData();
    } else if (selectedPhase?.deleted) {
      setFormsData([]);
      setFormName("");
      setLoading(false);
    }
  }, [setLoading, selectedPhase, formToggleLoader, studyId]);

  const handleSelectedPhase = (item: StudyTabsPhaseData) => {
    setSelectedPhase({
      id: item?.id,
      type: type.val,
      eventType: item?.type,
      deleted: false,
    });
  };

  const phaseRefresh = () => {
    setPhaseToggleLoader((prev) => !prev);
  };

  const formRefresh = () => {
    setFormToggleLoader((prev) => !prev);
  };

  const handleMenuClick = (
    event: React.MouseEvent<HTMLElement>,
    menuData: any,
    name: string
  ) => {
    event.stopPropagation();
    setTriggerEvent(name);
    if (name === "phase") {
      setDeleteConfirmation({
        title: `Delete ${
          type.val === "visit"
            ? "Visit"
            : type.val === "repeated_data"
            ? "Repeating Data"
            : "Survey"
        } ?`,
        description: `Are you sure you want to delete ${menuData?.name} ?`,
      });
    } else if (name === "form") {
      setDeleteConfirmation({
        title: "Delete Form ?",
        description: `Are you sure you want to delete ${menuData?.name} ?`,
      });
    }
    setRowData(menuData);
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setRowData(null);
  };

  const openModal = async (name: string) => {
    if (name !== "print") {
      setShowModal(name);
    } else {
      try {
        await http.post(`/study/${studyId}/print?type=${type?.val}`);
        window.open(`/print/study/${studyId}/phase/${type?.val}`, "_blank");
      } catch (err) {
        errorToastMessage(err as Error);
      }
    }
  };

  const closeModal = () => {
    setShowModal("");
    setRowData(null);
  };

  const handleDelete = async () => {
    try {
      handleMenuClose();
      setLoading(true);
      let url = `/study/${studyId}/study-phase`;
      let message: string = "";
      if (triggerEvent === "phase") {
        url += `/${rowData?.id}`;
      } else {
        url += `/${selectedPhase?.id}/phase_form/${rowData?.id}`;
      }

      await http.delete(url);
      if (triggerEvent === "phase") {
        phaseRefresh();
        if (selectedPhase?.id === rowData?.id) {
          setSelectedPhase({
            id: "",
            type: "",
            eventType: "",
            deleted: true,
          });
        }
        message = `${messageMap[type.val]} deleted successfully`;
      } else if (triggerEvent === "form") {
        formRefresh();
        message = "Form deleted successfully";
      }
      setShowDeleteModal(false);
      toastMessage("success", message);
    } catch (err) {
      errorToastMessage(err as Error);
      setLoading(false);
    }
  };
  const labelForOptions = (event: string, type: string) => {
    if (event === "phase") {
      if (type === "visit") {
        return "visit";
      } else if (type === "repeated_data") {
        return "repeating data";
      } else {
        return "survey";
      }
    } else {
      return "form";
    }
  };

  const formatExportDataIds = (eventType: string, type: string) => {
    const id = rowData?.id;
    const formIds = rowData?.formIds || [];
    const formId = rowData?.formId;
    const selectedPhaseId = selectedPhase?.id;

    const idsMap: any = {
      phase: {
        visit: {
          visitPhaseIds: [id],
          visitFormsIds: formIds,
        },
        repeated_data: {
          repeatedDataPhaseIds: [id],
          repeatedDataFormsIds: formIds,
        },
        survey: {
          surveyPhaseIds: [id],
          surveyFormsIds: formIds,
        },
      },
      form: {
        visit: {
          visitPhaseIds: [selectedPhaseId],
          visitFormsIds: [formId],
        },
        repeated_data: {
          repeatedDataPhaseIds: [selectedPhaseId],
          repeatedDataFormsIds: [formId],
        },
        survey: {
          surveyPhaseIds: [selectedPhaseId],
          surveyFormsIds: [formId],
        },
      },
    };

    return idsMap[eventType]?.[type] || {};
  };

  const handlePreviewClick = (formId: string) => {
    sessionStorage.setItem("selected-phase-id", selectedPhase?.id);
    navigate(`/studies/${studyId}/study-designer/form/${formId}/preview`);
  };

  const printForm = async () => {
    try {
      setAnchorEl(null);
      const formId = rowData?.formId;
      setRowData(null);
      await http.post(`/study/${studyId}/print?type=form&formId=${formId}`);
      window.open(`/print/study/${studyId}/form/${formId}`, "_blank");
    } catch (err) {
      errorToastMessage(err as Error);
    }
  };

  const handleExport = async (evetType: string, type: string) => {
    try {
      setLoading(true);
      const selectedForms = formatExportDataIds(evetType, type);
      const body = {
        selectedForms: selectedForms,
        exportData: "all",
      };
      let res;
      res = await http.post(`/study/${studyId}/exports`, body);
      toastMessage("success", res.data.message);
      setAnchorEl(null);
      setRowData(null);
      setLoading(false);
    } catch (err) {
      errorToastMessage(err as Error);
      setLoading(false);
    }
  };

  return (
    <>
      {loading && (
        <Backdrop
          open={true}
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        >
          <Loader />
        </Backdrop>
      )}
      <Box sx={StudyTabContent}>
        <Phases
          phaseData={phaseData}
          type={type}
          openModal={openModal}
          handleMenuClick={handleMenuClick}
          handleSelectedPhase={handleSelectedPhase}
          selectedPhase={selectedPhase?.id}
          canEdit={canEdit}
        />
        <Forms
          formName={formName}
          phaseData={phaseData}
          formsData={formsData}
          selectedPhase={selectedPhase?.id}
          eventType={selectedPhase?.eventType ?? ""}
          openModal={openModal}
          handleMenuClick={handleMenuClick}
          handlePreviewClick={handlePreviewClick}
          type={type}
          canEdit={canEdit}
        />
      </Box>

      <Menu anchorEl={anchorEl} open={open} onClose={handleMenuClose}>
        {canEdit && (
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              openModal(triggerEvent);
            }}
          >
            <ListItemText>
              Edit {labelForOptions(triggerEvent, type?.val)}
            </ListItemText>
          </MenuItem>
        )}
        {triggerEvent === "form" && (
          <MenuItem onClick={printForm}>
            <ListItemText>Print Form</ListItemText>
          </MenuItem>
        )}
        {triggerEvent === "form" && canEdit && (
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              sessionStorage.setItem("selected-phase-id", selectedPhase?.id);
              navigate(
                `/studies/${studyId}/study-designer/form/${rowData?.formId}/edit`
              );
            }}
          >
            <ListItemText>Open in form editor</ListItemText>
          </MenuItem>
        )}
        {triggerEvent === "form" && canEdit && (
          <MenuItem
            onClick={() => {
              console.log("rowData", rowData);
              if (rowData.questionCount > 0) {
                setAnchorEl(null);
                setShowModal("form-copy");
              } else {
                toastMessage(
                  "warning",
                  "The form has no questions. Please add at least one question before copying"
                );
              }
            }}
          >
            <ListItemText>Copy Form</ListItemText>
          </MenuItem>
        )}
        {triggerEvent === "form" && (
          <MenuItem
            onClick={() => {
              if (rowData.questionCount > 0) {
                setAnchorEl(null);
                setShowModal("save-template");
              } else {
                toastMessage(
                  "warning",
                  "The form has no questions. Please add at least one question saving the template"
                );
              }
            }}
          >
            <ListItemText>Save as Template</ListItemText>
          </MenuItem>
        )}
        {triggerEvent === "form" && languages.length > 1 && (
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              setShowModal("translation");
            }}
          >
            <ListItemText>View Translation</ListItemText>
          </MenuItem>
        )}
        {canExport && (
          <MenuItem onClick={() => handleExport(triggerEvent, type?.val)}>
            <ListItemText>
              Export {labelForOptions(triggerEvent, type?.val)}
            </ListItemText>
          </MenuItem>
        )}
        {canEdit && (
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              setShowDeleteModal(true);
            }}
          >
            <ListItemText sx={{ color: "#F05252" }}>
              Delete {labelForOptions(triggerEvent, type?.val)}
            </ListItemText>
          </MenuItem>
        )}
      </Menu>

      {showModal === "phase" && type?.val === "visit" && (
        <AddVisitModal
          showModal={showModal === "phase"}
          closeModal={closeModal}
          data={rowData}
          studyId={studyId}
          refreshPage={phaseRefresh}
        />
      )}
      {showModal === "phase" && type?.val === "repeated_data" && (
        <AddRepeatingModal
          showModal={showModal === "phase"}
          closeModal={closeModal}
          data={rowData}
          studyId={studyId}
          refreshPage={phaseRefresh}
        />
      )}
      {showModal === "phase" && type?.val === "survey" && (
        <AddSurveyModal
          showModal={showModal === "phase"}
          closeModal={closeModal}
          data={rowData}
          studyId={studyId}
          refreshPage={phaseRefresh}
        />
      )}
      {showModal === "form" && (
        <AddPhaseFormModal
          showModal={showModal === "form"}
          closeModal={closeModal}
          data={{
            ...rowData,
            phaseName:
              type?.val === "repeated_data" && selectedPhase?.eventType
                ? repeatedDataTypes?.[selectedPhase?.eventType]
                : formName,
          }}
          showPackageSelection={
            type?.val !== "survey" && features?.[featuresMap.survey]
          }
          studyId={studyId}
          phaseId={selectedPhase?.id}
          formId={rowData?.formId}
          refreshPage={formRefresh}
        />
      )}
      {showModal === "form-copy" && (
        <CopyFormModal
          showModal={showModal === "form-copy"}
          closeModal={closeModal}
          studyId={studyId}
          data={rowData}
          refreshPage={formRefresh}
          formType={"study"}
        />
      )}

      {showModal === "translation" && (
        <LanguageTranslationModal
          showModal={showModal === "translation"}
          closeModal={closeModal}
          studyId={studyId}
          formId={rowData?.formId}
          canEdit={canEdit}
        />
      )}
      {(showModal === "rearrangePhases" || showModal === "rearrangeForms") && (
        <RearrangeModal
          closeModal={closeModal}
          type={showModal}
          items={showModal === "rearrangePhases" ? phaseData : formsData}
          studyId={studyId}
          phaseId={selectedPhase?.id}
          refreshPage={
            showModal === "rearrangePhases" ? phaseRefresh : formRefresh
          }
          category={type.val}
        />
      )}
      {showModal === "save-template" && (
        <AddTemplateModal
          showModal={showModal === "save-template"}
          closeModal={closeModal}
          studyId={studyId}
          data={rowData}
          refreshPage={formRefresh}
          formId={rowData?.formId}
          type="crf"
        />
      )}
      <DeleteConfirmModal
        title={deleteConfirmation.title}
        description={deleteConfirmation.description}
        onOk={handleDelete}
        show={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          handleMenuClose();
        }}
      />
    </>
  );
};

export default StudyTab;
